/**
 * Which credential goes out on which request — the precedence rule, as one pure
 * function.
 *
 * Three token shapes can be in this app's hands at the same time:
 *
 *   1. the **user JWT** of this window's hub session (`authStore.token`);
 *   2. an **enrolled device credential** (PLAN.md F5, `typ:"device"`) for this
 *      connection — the shape `services/deviceCredential.ts` owns;
 *   3. a **panel credential** — what a device that paired as a *panel* holds
 *      (`/auth/pair-device`, role `panel`). It acts for the panel slot, not for
 *      a user, and it is the credential that keeps a wall panel operating with
 *      no account and no cloud (POSITIONING.md, Consequence 4).
 *
 * They must never shadow each other. This codebase has already been bitten
 * once: a stale `touch_device_token` (plus a stale `touch_panel_mode=true`) on a
 * *desktop* app answered for EVERY request, so the hub refused the panel token
 * on user routes with "Invalid token" and the silent refresh path was disabled
 * for panel runtime — one interrupted pairing took the whole session with it.
 * Adding a *parallel* panel surface (the miniapp) puts one more participant on
 * that same wire, so the rule is written down here, once, as a predicate that
 * can be tested without a browser:
 *
 *   1. an explicit `callerToken` wins — a caller that owns its credential says
 *      so, and nothing overrides it;
 *   2. the **panel credential** goes out only for a request that belongs to a
 *      panel scope, and only on the route families the hub accepts a device
 *      credential on (`/devices`, `/rooms`, `/scenes`, … plus the panel's own
 *      `/touch-panels/*` and `/auth/panel/refresh-token`);
 *   3. otherwise the **user JWT** — never the panel credential. A stale panel
 *      credential present while a user session is live is *withheld* on every
 *      user route (`/users/me`, `/auth/login`, `/notifications`, …);
 *   4. with no user JWT: the **enrolled device credential**, and only where the
 *      hub accepts it (`isDeviceReachableEndpoint`).
 *
 * "Belongs to a panel scope" is never ambient time. It is either declared by the
 * caller (a panel surface passes its own scope with the request) or the window
 * itself IS a panel: a device explicitly configured as one, with the panel scope
 * mounted (`config/panelDevice.ts` decides that from configuration, not from
 * hardware).
 */
import { DEVICE_REACHABLE_PREFIXES, isDeviceReachableEndpoint, normalizeEndpointPath } from './deviceCredential';

/** Which credential a request carries. */
export type CredentialShape = 'caller' | 'panel' | 'user' | 'device' | 'none';

/**
 * A panel context that owns a credential.
 *
 * `takeover` is the wall panel: the panel *is* the window, so every request the
 * window makes belongs to it. `app` is the miniapp: a panel surface running
 * beside a normal session, which declares its scope per call — it never becomes
 * the window's default, and closing it drops the credential.
 */
export type PanelScopeMode = 'takeover' | 'app';

export interface PanelScope {
  /** Stable id of this scope instance (`takeover`, `app:<n>`). */
  id: string;
  mode: PanelScopeMode;
  /** The panel credential itself. Held in memory for a miniapp. */
  credential: string | null;
  /** The panel slot this scope is paired to, when known. */
  panelId?: string | null;
  /** The role the hub gives a panel credential (`panel`). */
  role?: string | null;
}

export interface CredentialRequest {
  /** Endpoint the request targets: `/devices`, `/api/rooms?x=1` or an absolute URL. */
  endpoint: string;
  /** A credential the caller owns (highest precedence). */
  callerToken?: string | null;
  /** The panel scope this request belongs to, when the caller declares one. */
  ownerScope?: PanelScope | null;
  /** The panel scope of the whole window, when this window IS a panel. */
  windowScope?: PanelScope | null;
  /** User JWT of this window's hub session. */
  userToken?: string | null;
  /** Enrolled device credential (F5) for this connection. */
  deviceCredential?: string | null;
}

export interface CredentialDecision {
  shape: CredentialShape;
  token: string | null;
  /** Why this credential — carried into the API log so a wrong one is diagnosable. */
  reason: string;
  /**
   * A panel credential was present and deliberately NOT offered, because the
   * route does not accept one. This is the flag the old bug would have set on
   * every user route.
   */
  panelWithheld: boolean;
}

// ─── Route classes ───────────────────────────────────────────────────────────

/**
 * Routes that belong to a panel/play *surface* (the kiosk layout and the
 * miniapp's own pages). A request made while the app is on one of these belongs
 * to the panel surface, not to the user session.
 */
export const PANEL_SURFACE_PREFIXES = ['/panel', '/play'] as const;

export function isPanelSurfacePath(path: string): boolean {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return PANEL_SURFACE_PREFIXES.some((prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`));
}

/**
 * Route families that accept a PANEL credential.
 *
 * Grounded in the hub: `src/auth/index.ts` mounts `authenticateAny` on the
 * device families (`DEVICE_REACHABLE_PREFIXES`) and the panel credential is a
 * device credential to that verifier. On top of them a panel needs its own
 * surface (`/touch-panels/*`) and its own rotation route
 * (`/auth/panel/refresh-token`) — and nothing else: `/auth/panel/generate-pin`
 * is an *admin* route (`authenticate` + `authorize('admin')`), so a panel
 * credential offered there is the 401 this list exists to prevent.
 */
export const PANEL_CREDENTIAL_PREFIXES = [
  // A device credential's own lifecycle (`/auth/device/refresh`, `/auth/devices`).
  ...DEVICE_REACHABLE_PREFIXES,
  // The panel's surface: its pages, widgets, config, commands.
  '/touch-panels',
  // The panel credential's own rotation route.
  '/auth/panel/refresh-token',
] as const;

/** May this request carry a PANEL credential? */
export function isPanelCredentialReachable(endpoint: string): boolean {
  const path = normalizeEndpointPath(endpoint);
  if (!path) return false;
  return PANEL_CREDENTIAL_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`),
  );
}

/**
 * The three classes a request can fall into, as this client sees them:
 *
 * - `panel-surface` — a route the panel surface owns (`/panel`, `/play`);
 * - `panel-reachable` — a hub route that would accept a panel credential;
 * - `user` — anything else, i.e. a route only a user JWT answers.
 */
export type RouteClass = 'panel-surface' | 'panel-reachable' | 'user';

export function classifyRoute(endpoint: string): RouteClass {
  const path = normalizeEndpointPath(endpoint);
  if (path && isPanelSurfacePath(path)) return 'panel-surface';
  return isPanelCredentialReachable(endpoint) ? 'panel-reachable' : 'user';
}

// ─── The precedence rule ─────────────────────────────────────────────────────

export function selectRequestCredential(request: CredentialRequest): CredentialDecision {
  // 1. A caller that owns its credential says so, and keeps it.
  const callerToken = request.callerToken ?? null;
  if (callerToken) {
    return {
      shape: 'caller',
      token: callerToken,
      reason: 'caller-supplied credential',
      panelWithheld: false,
    };
  }

  const scope = request.ownerScope ?? request.windowScope ?? null;
  const panelToken = scope?.credential ?? null;
  const panelReachable = isPanelCredentialReachable(request.endpoint);

  // 2. The panel credential: only for a panel scope, only where the hub takes it.
  if (panelToken && scope && panelReachable) {
    return {
      shape: 'panel',
      token: panelToken,
      reason: `${scope.mode} panel scope, panel-reachable route`,
      panelWithheld: false,
    };
  }

  // 3. The user JWT. This is the branch a stale panel credential must land in —
  //    a panel credential is never offered to a route that would 401 on it.
  const userToken = request.userToken ?? null;
  if (userToken) {
    return {
      shape: 'user',
      token: userToken,
      reason: 'user session',
      panelWithheld: Boolean(panelToken),
    };
  }

  // 4. The enrolled device credential, where the hub accepts it.
  const deviceToken = request.deviceCredential ?? null;
  if (deviceToken && isDeviceReachableEndpoint(request.endpoint)) {
    return {
      shape: 'device',
      token: deviceToken,
      reason: 'enrolled device credential',
      panelWithheld: Boolean(panelToken),
    };
  }

  return {
    shape: 'none',
    token: null,
    reason: panelToken
      ? 'only a panel credential was present, and this route does not take one'
      : 'no credential present',
    panelWithheld: Boolean(panelToken),
  };
}
